// src/config/authConfig.jsx

// Supported platforms for authentication
export const SUPPORTED_PLATFORMS = ['google', 'facebook', 'twitter', 'github', 'telegram'];

// API Base URL - always points to Firebase Functions
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://anivyo-60fc8.web.app';

// Authentication endpoints - aligned with Firebase rewrite rules
export const AUTH_ENDPOINTS = {
  // Social authentication
  socialAuthentication: '/socialAuthentication', 
  telegramCallback: '/auth/telegram/callback',

  // OAuth endpoints
  initiateOAuth: '/auth/oauth/initiate',
  twitterCallback: '/auth/twitter/callback',
  githubCallback: '/auth/github/callback',
  facebookCallback: '/auth/facebook/callback',

  // Social media management
  linkSocialMedia: '/linkSocialMedia',
  unlinkSocialMedia: '/unlinkSocialMedia',
  getSocialMediaStats: '/getSocialMediaStats',

  // User management
  getUserProfile: '/getUserProfile',
  updateUserProfile: '/updateUserProfile',
  saveScore: '/saveScore',
  leaderboard: '/leaderboard',
  getReferralInfo: '/getReferralInfo',
  finalizeLogin: '/finalizeLogin',
  emailPasswordSignUp: '/emailPasswordSignUp',

  // Session management
  refreshToken: '/refreshToken',
  logout: '/logout',

  // Additional endpoints
  telegramWebhook: '/api/telegram-webhook',
  protectedRoute: '/protectedRoute'
};

// Platform-specific configurations with full callback URLs
export const PLATFORM_CONFIG = {
  telegram: {
    // botUsername must be defined in your .env file
    botUsername: import.meta.env.VITE_TELEGRAM_BOT_USERNAME || 'anivyo_bot',
    widgetUrl: 'https://telegram.org/js/telegram-widget.js?22',
    callbackUrl: `${API_BASE_URL}/auth/telegram/callback`
  },
  twitter: {
    callbackUrl: `${API_BASE_URL}/auth/twitter/callback`
  },
  github: {
    callbackUrl: `${API_BASE_URL}/auth/github/callback`
  },
  facebook: {
    callbackUrl: `${API_BASE_URL}/auth/facebook/callback`
  }
};

// Authentication configuration settings
export const AUTH_CONFIG = {
  tokenStorageKey: 'authToken',
  refreshTokenStorageKey: 'refreshToken',
  sessionTimeout: 86400000, // 24 hours
  refreshThreshold: 300000, // 5 minutes before session expiration
  maxRetries: 3,
  retryDelay: 1000, // Retry delay in ms
};

// Request configuration for API calls
export const REQUEST_CONFIG = {
  timeout: 15000, // 15 seconds timeout
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
};

// Error message configurations
export const ERROR_MESSAGES = {
  invalidToken: 'Invalid or expired authentication token',
  networkError: 'Network error occurred. Please check your connection',
  unauthorized: 'Unauthorized access. Please log in again',
  serverError: 'Server error occurred. Please try again later',
  invalidPlatform: (platform) => `Unsupported platform: ${platform}`,
  authFailed: (platform) => `Authentication failed for ${platform}`,
  timeout: 'Request timed out. Please try again',
  invalidResponse: 'Invalid response received from server',
  noData: 'No data received from server'
};

// Environment-specific configurations
export const ENV_CONFIG = {
  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
  firebaseConfig: {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
  }
};

// Utility functions
export const getEndpointUrl = (endpoint) => `${API_BASE_URL}${endpoint}`;

// Function to validate if a platform is supported
export const isValidPlatform = (platform) => SUPPORTED_PLATFORMS.includes(platform);

// Retrieve platform-specific configurations
export const getPlatformConfig = (platform) => {
  if (!isValidPlatform(platform)) {
    throw new Error(ERROR_MESSAGES.invalidPlatform(platform));
  }
  return PLATFORM_CONFIG[platform];
};